import { useState, useEffect, useRef } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useLanguage } from "../../contexts/LanguageContext";
import { SectionCTA } from "../shared/SectionCTA";

export const TestimonialsSection = () => {
  const { language, translations: t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonialsData = t.testimonials.items.map((item, i) => ({
    ...item,
    id: i + 1,
    rating: 5,
    avatar: item.name.charAt(0)
  }));

  const nextTestimonial = () => setCurrentIndex((prev) => (prev + 1) % testimonialsData.length);
  const prevTestimonial = () => setCurrentIndex((prev) => (prev - 1 + testimonialsData.length) % testimonialsData.length);

  useEffect(() => {
    const timer = setInterval(nextTestimonial, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="testimonials" className="py-20 md:py-28 bg-white scroll-mt-20" data-testid="testimonials-section" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
        >
          <span className="text-[#C4956A] font-semibold mb-2 block">{t.testimonials.label}</span>
          <h2 className="font-['Heebo'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2C2C] mb-4">
            {t.testimonials.title}
          </h2>
          <div className="w-20 h-1 bg-[#C4956A] mx-auto" />
        </motion.div>

        <div className="relative mb-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              className="bg-[#FAF7F2] rounded-3xl p-8 md:p-12 relative"
            >
              <Quote className="absolute top-8 right-8 w-16 h-16 text-[#C4956A]/10" />
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonialsData[currentIndex].rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#C4956A] text-[#C4956A]" />
                ))}
              </div>
              <p className="text-xl md:text-2xl text-[#2C2C2C] mb-8 leading-relaxed">
                {testimonialsData[currentIndex].text}
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#C4956A] flex items-center justify-center text-white text-xl font-bold">
                  {testimonialsData[currentIndex].avatar}
                </div>
                <div>
                  <div className="font-bold text-[#2C2C2C]">{testimonialsData[currentIndex].name}</div>
                  <div className="text-[#2C2C2C]/60">{testimonialsData[currentIndex].city}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-[#C4956A] hover:text-white transition-all"
              data-testid="testimonial-prev"
              aria-label={language === 'he' ? "המלצה קודמת" : "Previous testimonial"}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <div className="flex gap-2">
              {testimonialsData.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-3 h-3 rounded-full transition-all ${i === currentIndex ? 'bg-[#C4956A] w-6' : 'bg-[#C4956A]/30'}`}
                  aria-label={`${language === 'he' ? 'עבור להמלצה' : 'Go to testimonial'} ${i + 1}`}
                />
              ))}
            </div>
            <button
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-[#C4956A] hover:text-white transition-all"
              data-testid="testimonial-next"
              aria-label={language === 'he' ? "המלצה הבאה" : "Next testimonial"}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonialsData.slice(0, 3).map((testimonial, i) => (
            <motion.div
              key={testimonial.id}
              className="bg-[#FAF7F2] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.1 }}
            >
              <div className="flex items-center gap-1 mb-3">
                {[...Array(testimonial.rating)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-[#C4956A] text-[#C4956A]" />
                ))}
              </div>
              <p className="text-[#2C2C2C]/80 mb-4 text-sm leading-relaxed">{testimonial.text}</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#C4956A] flex items-center justify-center text-white font-bold">
                  {testimonial.avatar}
                </div>
                <div>
                  <div className="font-semibold text-[#2C2C2C] text-sm">{testimonial.name}</div>
                  <div className="text-[#2C2C2C]/60 text-xs">{testimonial.city}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <SectionCTA 
          text={language === 'he' ? "רוצים להצטרף ללקוחות המרוצים שלנו?" : "Want to join our happy clients?"} 
          buttonText={language === 'he' ? "קבעו ייעוץ" : "Book consultation"} 
        />
      </div>
    </section>
  );
};
